import type {
  ClaimCertification,
  ClaimCertificationFinding,
  ClaimWorkFindingHighlight,
  ClaimWorkItem,
} from '../../../store';

/**
 * What the Issues popover lists, read off the claim work row and, once it
 * has arrived, the claim's certification (GDN-3379). Pure: no hooks and no
 * fetching, so a test hands it a row and reads the list.
 *
 * The row speaks first and the certification second. The row's highlights,
 * submission blockers and legacy blocker are on screen the moment the
 * popover opens; the certification's remaining findings append when its
 * read lands. A highlight names the same finding the certification carries
 * (`finding_key`), so the two are joined rather than listed twice, and the
 * join lends the highlight the finding's `policy_title` as its title. A
 * highlight's `observation` is the fail/hold/info verdict, not a sentence,
 * so it is never printed; `why` is the sentence.
 */

export interface IssueDetailItem {
  key: string;
  /** Wears the error icon; everything else wears the warning icon. */
  blocking: boolean;
  title: string;
  why: string | null;
  remediation: string | null;
}

export interface IssueCounts {
  blockers: number;
  warnings: number;
  findings: number;
}

export interface IssueDetailModel {
  /** Every non-zero count, blockers first, as "2 blockers". */
  counts: string[];
  items: IssueDetailItem[];
  /** Items past the cap, named in one line rather than listed. */
  more: number;
}

/** A popover is a glance, not a page: past this many, the rest is a count. */
export const ISSUE_DETAIL_ITEM_CAP = 8;

/**
 * The counts the Home cell leads with: the certification's active counts
 * where the server sends them, the submission and legacy validation counts
 * where it does not.
 */
export const readIssueCounts = (item: ClaimWorkItem): IssueCounts => ({
  blockers: item.active_blocker_count ?? item.submission_blocker_count ?? 0,
  findings: item.active_finding_count ?? 0,
  warnings: item.active_advisory_count ?? item.legacy_warning_count ?? 0,
});

const pluralize = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

export const formatIssueCounts = (counts: IssueCounts): string[] => {
  const parts: string[] = [];
  if (counts.blockers > 0) parts.push(pluralize(counts.blockers, 'blocker'));
  if (counts.warnings > 0) parts.push(pluralize(counts.warnings, 'warning'));
  if (counts.findings > 0) parts.push(pluralize(counts.findings, 'finding'));
  return parts;
};

/** Home's headline: the first count that is not zero, or "0 findings". */
export const formatIssueHeadline = (counts: IssueCounts): string =>
  formatIssueCounts(counts)[0] ?? pluralize(counts.findings, 'finding');

const text = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

/* `remediation` is an open record on the wire (`{ [key: string]: any }` on
   both the highlight and the certification finding, no named fields), so the
   text is read under the keys producers are known to use, the ones the
   workbench reads too. */
const REMEDIATION_KEYS = ['instruction', 'summary', 'action', 'message'];

const remediationText = (
  remediation: Record<string, unknown> | null | undefined,
): string | null => {
  if (!remediation) return null;
  for (const key of REMEDIATION_KEYS) {
    const value = remediation[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

/** "cert.scrub.line-rendering-npi-required" reads as words when no title exists. */
const policyName = (policyId: string): string =>
  policyId.replace(/^cert\./, '').replace(/[._-]+/g, ' ');

const highlightItem = (
  highlight: ClaimWorkFindingHighlight,
  finding: ClaimCertificationFinding | undefined,
): IssueDetailItem => {
  const why = text(highlight.why) ?? text(finding?.why);
  const title = text(finding?.policy_title) ?? why ?? policyName(highlight.policy_id);
  return {
    blocking:
      highlight.blocks_submission ?? finding?.certification_effect?.blocks_submission ?? false,
    key: `finding:${highlight.finding_key}`,
    remediation: remediationText(highlight.remediation) ?? remediationText(finding?.remediation),
    title,
    why: why === title ? null : why,
  };
};

const findingItem = (finding: ClaimCertificationFinding): IssueDetailItem => ({
  blocking: finding.certification_effect?.blocks_submission ?? false,
  key: `finding:${finding.finding_key}`,
  remediation: remediationText(finding.remediation),
  title: text(finding.policy_title) ?? policyName(finding.policy_id),
  why: text(finding.why),
});

const blockerItem = (message: string, index: number): IssueDetailItem => ({
  blocking: true,
  key: `blocker:${index}`,
  remediation: null,
  title: message,
  why: null,
});

/** The row's counts exclude dismissed and resolved findings, and so does this list. */
const isActiveFinding = (finding: ClaimCertificationFinding): boolean =>
  finding.disposition_state !== 'dismissed' && finding.disposition_state !== 'resolved';

/** Blockers first, then suggestions: the certification's own order. */
const certificationFindings = (
  certification: ClaimCertification | null | undefined,
): ClaimCertificationFinding[] => [
  ...(certification?.blockers ?? []),
  ...(certification?.suggestions ?? []),
];

/** The full submission blocker list, or the top one when the row carries only that. */
const submissionBlockers = (item: ClaimWorkItem): string[] => {
  if (item.submission_blockers?.length) return item.submission_blockers;
  return item.submission_top_blocker ? [item.submission_top_blocker] : [];
};

export const buildIssueDetail = (
  item: ClaimWorkItem,
  certification?: ClaimCertification | null,
): IssueDetailModel => {
  const findings = certificationFindings(certification);
  const byKey = new Map(findings.map(finding => [finding.finding_key, finding]));
  const highlights = item.finding_highlights ?? [];
  const shown = new Set(highlights.map(highlight => highlight.finding_key));
  const items = [
    ...highlights.map(highlight => highlightItem(highlight, byKey.get(highlight.finding_key))),
    ...submissionBlockers(item).map(blockerItem),
    ...findings
      .filter(finding => isActiveFinding(finding) && !shown.has(finding.finding_key))
      .map(findingItem),
  ];
  const listed =
    items.length === 0 && item.legacy_top_blocker
      ? [blockerItem(item.legacy_top_blocker, 0)]
      : items;
  return {
    counts: formatIssueCounts(readIssueCounts(item)),
    items: listed.slice(0, ISSUE_DETAIL_ITEM_CAP),
    more: Math.max(0, listed.length - ISSUE_DETAIL_ITEM_CAP),
  };
};

/** Whether the row gives the popover anything to say before any read. */
export const hasIssueDetail = (item: ClaimWorkItem): boolean => {
  const model = buildIssueDetail(item);
  return model.items.length > 0 || model.counts.length > 0;
};
